// @/components/providers/PostHogProvider.jsx

/**
 * PostHog Provider Component
 * 
 * Conditional PostHog provider that only initializes when user consents.
 * Follows PostHog React documentation patterns with GDPR compliance.
 * 
 * Features:
 * - Conditional initialization based on consent
 * - Dynamic opt-in/opt-out handling
 * - Proper cleanup and state management
 * - Context provider for usePostHog hook
 * - Error boundaries and fallbacks
 * 
 * Based on PostHog React docs:
 * https://posthog.com/docs/libraries/react
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { postHogService } from '@/services/analytics/posthog';

// Create PostHog context
const PostHogContext = createContext(null);

/**
 * PostHog Provider Component
 * Conditionally provides PostHog instance based on user consent
 */
export const PostHogProvider = ({ children }) => {
  const [posthogInstance, setPosthogInstance] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  /**
   * Handle consent changes and update PostHog state
   */
  const handleConsentChange = useCallback((event) => {
    const { enabled } = event.detail;
    setConsentGiven(enabled);

    if (enabled && postHogService.isReady()) {
      setPosthogInstance(postHogService.getPostHogInstance());
      setIsReady(true);
    } else if (!enabled) {
      setPosthogInstance(null);
      setIsReady(false);
    }
  }, []);

  /**
   * Check PostHog service status
   */
  const checkServiceStatus = useCallback(() => {
    const consent = localStorage.getItem('analyticsConsent') === 'true';
    setConsentGiven(consent);

    if (consent && postHogService.isReady()) {
      setPosthogInstance(postHogService.getPostHogInstance());
      setIsReady(true);
    }
  }, []);

  // Initialize PostHog service and set up listeners
  useEffect(() => {
    // Initialize the service
    postHogService.initialize().then(() => {
      checkServiceStatus();
    });

    // Listen for consent changes
    window.addEventListener('analyticsConsentChanged', handleConsentChange);

    // Listen for service ready events (in case service initializes later)
    const handleServiceReady = () => {
      checkServiceStatus();
    };

    window.addEventListener('posthogServiceReady', handleServiceReady);

    // Cleanup
    return () => {
      window.removeEventListener('analyticsConsentChanged', handleConsentChange);
      window.removeEventListener('posthogServiceReady', handleServiceReady);
    };
  }, [handleConsentChange, checkServiceStatus]);

  // Context value
  const contextValue = {
    posthog: posthogInstance,
    isReady,
    consentGiven,
    service: postHogService
  };

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  );
};

/**
 * usePostHog Hook
 * Returns PostHog instance (or null if consent not given)
 * Follows PostHog React docs pattern with proper typing
 */
export const usePostHog = () => {
  const context = useContext(PostHogContext);

  // No provider in the tree — e.g. Gatsby's static-HTML SSR pass, where the
  // provider (which reads localStorage) can't mount. Degrade to "no analytics"
  // instead of crashing the build. createContext's default is null, so guard
  // both null and undefined.
  if (!context) return null;

  // Return null if no consent (following docs pattern for conditional usage)
  return context.consentGiven ? context.posthog : null;
};

/**
 * usePostHogService Hook
 * Returns the PostHog service for advanced usage
 */
export const usePostHogService = () => {
  const context = useContext(PostHogContext);

  // No provider (SSR / static build) — fall back to the singleton service. Its
  // methods no-op until it's initialised and consented client-side, so callers
  // like useAnalytics().trackEvent stay safe during the server render.
  if (!context) return postHogService;

  return context.service;
};

/**
 * useAnalytics Hook
 * Convenience hook for common analytics operations
 */
export const useAnalytics = () => {
  const posthog = usePostHog();
  const service = usePostHogService();

  const trackEvent = useCallback((eventName, properties = {}) => {
    service.trackEvent(eventName, properties);
  }, [service]);

  const trackPageView = useCallback((url, properties = {}) => {
    service.trackPageView(url, properties);
  }, [service]);

  const trackFeatureUsage = useCallback((feature, action, properties = {}) => {
    service.trackFeatureUsage(feature, action, properties);
  }, [service]);

  const trackEngagement = useCallback((type, properties = {}) => {
    service.trackEngagement(type, properties);
  }, [service]);

  const identifyUser = useCallback(() => {
    service.identifyUser();
  }, [service]);

  return {
    // PostHog instance (null if no consent)
    posthog,

    // Service methods (always available, handle consent internally)
    trackEvent,
    trackPageView,
    trackFeatureUsage,
    trackEngagement,
    identifyUser,

    // Status
    isReady: service.isReady(),
    consentGiven: service.consentGiven
  };
};

export default PostHogProvider;